import { Box, Typography } from "@mui/material";
import Loading from "../Loading/Loading";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

export type ActionButtonVariant = "primary" | "secondary" | "danger";

/**
 * The app's action button.
 *
 * Matches the styling already used by the newer screens (the video scheduling
 * dialog, the patient flows): 48px tall, fully rounded, the brand gradient for
 * the primary action and an outline for the secondary one. All values come from
 * the design tokens — nothing here is a literal colour or size.
 *
 * This is separate from the older `ButtonComponent`, which is still used by the
 * plans and CMD screens with a different look.
 */
const baseSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  height: "48px",
  px: "24px",
  borderRadius: "40px",
  textTransform: "none" as const,
  // Rendered as an anchor for downloads, where the default underline would
  // make it read as a link rather than a button.
  textDecoration: "none"
};

interface VariantStyle {
  border: string;
  background: string;
  /** Background once the button is disabled or loading. */
  disabledBackground: string;
  hoverBackground?: string;
  labelColor: string;
}

/**
 * One entry per variant, so the variant names live here alone instead of being
 * matched against string literals scattered through the component.
 */
const variantStyles: Record<ActionButtonVariant, VariantStyle> = {
  primary: {
    border: "none",
    background: semanticColors.gradient.surface,
    disabledBackground: semanticColors.surface.container,
    labelColor: semanticColors.text.inverse
  },
  secondary: {
    border: `1.5px solid ${semanticColors.border.strong}`,
    background: "transparent",
    disabledBackground: "transparent",
    hoverBackground: semanticColors.surface.containerLow,
    labelColor: semanticColors.text.primary
  },
  danger: {
    border: "none",
    background: semanticColors.feedback.danger,
    disabledBackground: semanticColors.surface.container,
    labelColor: semanticColors.text.inverse
  }
};

const variantSx = (style: VariantStyle, disabled: boolean) => ({
  border: style.border,
  background: disabled ? style.disabledBackground : style.background,
  ...(style.hoverBackground
    ? { "&:hover": { background: style.hoverBackground } }
    : {})
});

export default function ActionButton({
  label,
  onClick,
  variant = "primary",
  disabled = false,
  loading = false,
  fullWidth = false,
  startIcon,
  href,
  download
}: {
  label: string;
  onClick?: () => void;
  variant?: ActionButtonVariant;
  disabled?: boolean;
  /** Shows a spinner in place of the label; also blocks the click. */
  loading?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  /**
   * Renders the button as a link. Needed for downloads: only a real anchor
   * carries `download`, and a click handler that fakes one is what pop-up
   * blockers and browser download rules treat with suspicion.
   */
  href?: string;
  /** The file name offered when saving. Only meaningful alongside `href`. */
  download?: string;
}) {
  const isDisabled = disabled || loading;
  const style = variantStyles[variant];
  const asLink = Boolean(href);

  return (
    <Box
      component={asLink ? "a" : "button"}
      type={asLink ? undefined : "button"}
      href={href}
      download={download}
      onClick={isDisabled ? undefined : onClick}
      disabled={asLink ? undefined : isDisabled}
      sx={{
        ...baseSx,
        ...variantSx(style, isDisabled),
        width: fullWidth ? "100%" : "auto",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.6 : 1
      }}
    >
      {loading ? (
        <Loading width={24} height={24} color={style.labelColor} />
      ) : (
        <>
          {startIcon}
          <Typography
            sx={{ ...typography.body.lBold, color: style.labelColor }}
          >
            {label}
          </Typography>
        </>
      )}
    </Box>
  );
}
